import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import moment from "moment";
import { protectedApi } from '../../../app/utils/common/protectedApi'
import { APIS } from '../../../app/utils/constants/apiConstant'
import { getDashboardTodayAttendanceAPI } from '../../../Redux/Apis/attendanceApi'
import {
    formatPunchDisplay,
    getTodayMorningInPunch,
} from '../../../app/utils/common/attendancePunchDisplay'
import ProjectTimeSpent from '../../molecules/Project-time-spent/Project-time-spent'
import EmployeeLeave from '../../molecules/DashboardComponents/EmployeeLeave/EmployeeLeave'
import UpcomingBirthdays from '../../molecules/DashboardComponents/UpcomingBirthdays/UpcomingBirthdays'
import UpcomingAnniversary from '../../molecules/DashboardComponents/UpcomingAnniversary/UpcomingAnniversary'
import DashboardCelebrations from '../../molecules/DashboardCelebrations/DashboardCelebrations'
import DashboardPraiseBanner from '../../molecules/DashboardPraiseBanner/DashboardPraiseBanner'
import DashboardSkeleton from '../../molecules/DashboardSkeleton/DashboardSkeleton'
import "./DashboardPage.scss";
import { getAllProject } from '../../../Redux/Apis/projectApi'
import { getEmployeesAPI, getEmployeeDirectoryAPI } from '../../../Redux/Apis/employeeApi'
import { getTasksByDateRangeAPI } from '../../../Redux/Apis/taskApi'
import { getUserLeaveRequestAPI } from '../../../Redux/Apis/leaveApi'
import { getTeamOnLeaveAPI } from '../../../Redux/Apis/dashboard'
import {
    checkPermission,
    permissionType,
    canAttendanceOverview,
    canReadLeaveManagement,
    hasAdminAccess,
    isCeoOrCmoRoleUser,
    getMissedDailyUpdatesLeaveSuffix,
    HR_ROLE_ID,
} from "../../../app/utils/common/Functions";
import {
    getLeaveInfoForUserDate,
    isFullLeaveDay,
    userMeetsDailyWorkRequirement,
} from "../../../app/utils/common/dailyWorkLeave";
import { isPublicHolidayDate } from "../../../app/utils/common/publicHolidays";
import { LEAVE_APPROVE_PENDING_REFRESH_EVENT } from "../../../app/utils/constants/apiConstant";
import { TEAM_PRAISE_UPDATED_EVENT } from "../../../app/utils/constants/teamPraiseEvents";
import { fetchBirthdaySentToday } from "../../../app/utils/common/birthdayEmail";
import { fetchWorkAnniversarySentToday } from "../../../app/utils/common/workAnniversaryEmail";

const DASHBOARD_POLL_MS = 90_000;
const DASHBOARD_EMPLOYEE_PAGE_SIZE = 500;
const PROJECT_DOC_PENDING_EVENT = "project-documentation-pending-count";

const DASHBOARD_KPI_ICONS = {
    projects: "/svg/Icons/project-icon.svg",
    leaves: "/svg/Icons/leave-icon.svg",
    leaveApprove: "/svg/Icons/leave-approve-icon.svg",
    missed: "/svg/Icons/hours-icon.svg",
    docs: "/svg/Icons/documentation-icon.svg",
};

const getDashboardGreeting = () => {
    const hour = moment().hour();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
};

/** Last workday to check for missed team updates (Mon → previous Friday; else yesterday if weekday). */
const getAdminMissedUpdatesCheckDate = (now = moment()) => {
    const today = moment(now);
    if (today.isoWeekday() === 1) {
        return today.clone().subtract(3, "days");
    }
    const yesterday = today.clone().subtract(1, "day");
    if (yesterday.isoWeekday() >= 6) return null;
    return yesterday;
};

const DashboardPage = ({ openLeaveDrawer, openHoursDrawer }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useSelector((state) => state.auth.auth);
    const { projects, loading: projectLoading } = useSelector((state) => state.project);
    const {
        pagination: employeePagination,
        employees,
        directoryUsers,
        loading: employeeLoading,
        directoryLoading,
    } = useSelector((state) => state.employee);
    const {
        calendarTasks,
        calendarLeaveByDate,
        calendarPublicHolidaysByDate,
        loading: calendarTasksLoading,
    } = useSelector((state) => state.task);
    const { pagination: leavePagination, loading: leaveLoading } = useSelector((state) => state.leave);
    const { dashboardTodayLogs, dashboardTodayLogsLoading } = useSelector((state) => state.attendance);
    const { loading: teamOnLeaveLoading } = useSelector((state) => state.dashboard);
    const isAdminRole = hasAdminAccess(user);
    const isHrRole = Number(user?.roleId) === HR_ROLE_ID;
    const isHrDashboard = isHrRole && !isAdminRole;
    const canSendCelebrationEmail =
        hasAdminAccess(user) || isHrRole;
    const canReadDailyHours = checkPermission(permissionType.DAILY_HOURS, "read");
    const canReadAllDailyHours = checkPermission(permissionType.DAILY_HOURS, "read users");
    const canReadLeaveRequests = canReadLeaveManagement();
    const [dashboardPraise, setDashboardPraise] = useState(null);
    const [birthdayEmailSentUserIds, setBirthdayEmailSentUserIds] = useState([]);
    const [anniversaryEmailSentUserIds, setAnniversaryEmailSentUserIds] = useState([]);
    /** Keeps yesterday's expected hours separate from Redux so the hours drawer does not overwrite it. */
    const [dashYesterdayWorkingHours, setDashYesterdayWorkingHours] = useState({
        loading: true,
        hours: 8,
    });
    const [adminLeavePendingCount, setAdminLeavePendingCount] = useState(0);
    const [adminLeavePendingLoading, setAdminLeavePendingLoading] = useState(false);
    const [adminProjectDocPendingCount, setAdminProjectDocPendingCount] = useState(0);
    const [adminProjectDocPendingLoading, setAdminProjectDocPendingLoading] = useState(false);
    const [adminOnLeaveLoading, setAdminOnLeaveLoading] = useState(false);
    const [dashboardApisTriggered, setDashboardApisTriggered] = useState(false);
    const [dashboardInitialLoadDone, setDashboardInitialLoadDone] = useState(false);
    const dashboardInitialLoadDoneRef = useRef(false);
    /** userId → leave suffix for missed-updates check date (e.g. "half day", "on leave"). */
    const [adminLeaveByUserId, setAdminLeaveByUserId] = useState({});

    const canFetchAdminLeavePending = Boolean(
        user && (isAdminRole || checkPermission(permissionType.LEAVE_APPROVE, "read"))
    );
    const canFetchAdminProjectDocPending = Boolean(
        user && (isAdminRole || checkPermission(permissionType.PROJECT_DOCUMENTATION, "update"))
    );
    const canFetchTeamDailyUpdates = Boolean(
        user && canReadDailyHours && (canReadAllDailyHours || isAdminRole)
    );

    const markBirthdayEmailSent = useCallback((userId) => {
        const id = Number(userId);
        if (!id) return;
        setBirthdayEmailSentUserIds((prev) =>
            prev.includes(id) ? prev : [...prev, id]
        );
    }, []);

    const markAnniversaryEmailSent = useCallback((userId) => {
        const id = Number(userId);
        if (!id) return;
        setAnniversaryEmailSentUserIds((prev) =>
            prev.includes(id) ? prev : [...prev, id]
        );
    }, []);

    const fetchAdminLeavePendingCount = useCallback(async ({ trackLoading = false } = {}) => {
        if (!canFetchAdminLeavePending) {
            setAdminLeavePendingCount(0);
            setAdminLeavePendingLoading(false);
            return;
        }
        if (trackLoading) setAdminLeavePendingLoading(true);
        try {
            const response = await protectedApi({
                url: `${APIS.GET_ALL_LEAVES}?page=1&limit=1&attention=1`,
                method: "GET",
            });
            const total = response?.data?.data?.pagination?.totalData;
            setAdminLeavePendingCount(typeof total === "number" ? total : 0);
        } catch {
            setAdminLeavePendingCount(0);
        } finally {
            if (trackLoading) setAdminLeavePendingLoading(false);
        }
    }, [canFetchAdminLeavePending]);

    const fetchAdminProjectDocPendingCount = useCallback(async ({ trackLoading = false } = {}) => {
        if (!canFetchAdminProjectDocPending) {
            setAdminProjectDocPendingCount(0);
            setAdminProjectDocPendingLoading(false);
            return;
        }
        if (trackLoading) setAdminProjectDocPendingLoading(true);
        try {
            const response = await protectedApi({
                url: APIS.PROJECT_DOCUMENTATION_EDIT_REQUESTS_PENDING,
                method: "GET",
            });
            const list = Array.isArray(response?.data?.data) ? response.data.data : [];
            setAdminProjectDocPendingCount(list.length);
        } catch {
            setAdminProjectDocPendingCount(0);
        } finally {
            if (trackLoading) setAdminProjectDocPendingLoading(false);
        }
    }, [canFetchAdminProjectDocPending]);

    const fetchAdminOnLeaveForCheckDate = useCallback(async (dateStr, { trackLoading = false } = {}) => {
        if (!isAdminRole || !dateStr) {
            setAdminLeaveByUserId({});
            setAdminOnLeaveLoading(false);
            return;
        }
        if (trackLoading) setAdminOnLeaveLoading(true);
        try {
            const response = await protectedApi({
                url: `${APIS.LEAVE_TEAM_ON_LEAVE}?date=${encodeURIComponent(dateStr)}`,
                method: "GET",
            });
            const requests =
                response?.data?.data?.leaveRequests ??
                response?.data?.leaveRequests ??
                [];
            const byUser = {};
            (Array.isArray(requests) ? requests : []).forEach((row) => {
                const id = String(row?.userId ?? "");
                if (!id) return;
                const suffix = getMissedDailyUpdatesLeaveSuffix(row?.leaveType);
                if (!byUser[id]) {
                    byUser[id] = suffix;
                    return;
                }
                // Prefer the more specific label when multiple rows exist for one user.
                const priority = { "on leave": 0, "half day": 1, "early leave": 2 };
                if ((priority[suffix] ?? 0) > (priority[byUser[id]] ?? 0)) {
                    byUser[id] = suffix;
                }
            });
            setAdminLeaveByUserId(byUser);
        } catch {
            setAdminLeaveByUserId({});
        } finally {
            if (trackLoading) setAdminOnLeaveLoading(false);
        }
    }, [isAdminRole]);

    const fetchDashboardPraise = useCallback(async () => {
        if (!user) return;
        if (Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true) {
            return;
        }
        try {
            const r = await protectedApi({ url: APIS.PRAISE_CURRENT, method: "GET" });
            const d = r?.data?.data;
            if (d && typeof d === "object") {
                setDashboardPraise(d);
            } else {
                setDashboardPraise({ message: "", praiseLabel: null, updatedAt: null, authorName: null });
            }
        } catch {
            setDashboardPraise(null);
        }
    }, [user]);

    const canEmployeeAttendanceInsights = () =>
        checkPermission(permissionType.ATTENDANCE, "read") || canAttendanceOverview();

    useEffect(() => {
        if (!user || location.pathname !== "/dashboard") return;
        if (Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true) {
            dashboardInitialLoadDoneRef.current = true;
            setDashboardInitialLoadDone(true);
            return;
        }

        dashboardInitialLoadDoneRef.current = false;
        setDashboardInitialLoadDone(false);
        setDashboardApisTriggered(false);

        const today = moment().format("YYYY-MM-DD");
        const monthStart = moment().startOf("month");
        const yesterday = moment().subtract(1, "day");
        const taskRangeStart = moment.min(monthStart, yesterday).format("YYYY-MM-DD");
        const canReadProjects = checkPermission(permissionType.PROJECT_MANAGEMENT, "read");
        const canReadUsers = checkPermission(permissionType.USER_MANAGEMENT, "read");
        if (!isAdminRole && canEmployeeAttendanceInsights()) {
            dispatch(getDashboardTodayAttendanceAPI());
        }

        if (canReadProjects) {
            dispatch(getAllProject());
        }

        if (canReadUsers) {
            dispatch(
                getEmployeesAPI({
                    page: 1,
                    limit: DASHBOARD_EMPLOYEE_PAGE_SIZE,
                    filterBy: "active",
                })
            );
        } else {
            dispatch(getEmployeeDirectoryAPI());
        }

        if (canReadDailyHours) {
            if (isAdminRole && canFetchTeamDailyUpdates) {
                const checkDate = getAdminMissedUpdatesCheckDate();
                if (checkDate) {
                    const checkDateStr = checkDate.format("YYYY-MM-DD");
                    dispatch(
                        getTasksByDateRangeAPI({
                            startDate: checkDateStr,
                            endDate: checkDateStr,
                            id: "all",
                        })
                    );
                    fetchAdminOnLeaveForCheckDate(checkDateStr, { trackLoading: true });
                }
            } else if (!isAdminRole) {
                dispatch(
                    getTasksByDateRangeAPI({
                        startDate: taskRangeStart,
                        endDate: today,
                        id: canReadAllDailyHours ? "all" : user?.id,
                    })
                );
            }
        }

        if (isAdminRole) {
            fetchAdminLeavePendingCount({ trackLoading: true });
            fetchAdminProjectDocPendingCount({ trackLoading: true });
        } else {
            if (canFetchAdminLeavePending) {
                fetchAdminLeavePendingCount({ trackLoading: true });
            }
            if (canReadLeaveRequests) {
                dispatch(getUserLeaveRequestAPI({ page: 1, limit: 10 }));
            }
        }

        dispatch(getTeamOnLeaveAPI({ date: today }));

        setDashboardApisTriggered(true);
    }, [
        dispatch,
        user,
        location.pathname,
        location.key,
        isAdminRole,
        canFetchAdminLeavePending,
        canReadLeaveRequests,
        canFetchTeamDailyUpdates,
        fetchAdminLeavePendingCount,
        fetchAdminProjectDocPendingCount,
        fetchAdminOnLeaveForCheckDate,
    ]);

    useEffect(() => {
        if (!user) return;
        if (Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true) {
            return;
        }
        if (!canReadDailyHours) {
            setDashYesterdayWorkingHours({ loading: false, hours: 8 });
            return;
        }
        let cancelled = false;
        const yStr = moment().subtract(1, "day").format("YYYY-MM-DD");
        setDashYesterdayWorkingHours((s) => ({ ...s, loading: true }));

        protectedApi({
            url: `/api/task/getWorkingHours?date=${encodeURIComponent(yStr)}`,
            method: "GET",
        })
            .then((r) => {
                const raw = r?.data?.data;
                const h =
                    typeof raw === "number"
                        ? raw
                        : typeof raw?.workingHours === "number"
                          ? raw.workingHours
                          : 8;
                if (cancelled) return;
                setDashYesterdayWorkingHours({
                    loading: false,
                    hours: !Number.isNaN(h) ? h : 8,
                });
            })
            .catch(() => {
                if (!cancelled) {
                    setDashYesterdayWorkingHours({ loading: false, hours: 8 });
                }
            });

        return () => {
            cancelled = true;
        };
    }, [user, canReadDailyHours]);

    const canReadUserMgmtForLoading = checkPermission(permissionType.USER_MANAGEMENT, "read");
    const canReadProjectsForLoading = checkPermission(permissionType.PROJECT_MANAGEMENT, "read");

    const isAnyDashboardApiPending = useMemo(() => {
        if (!user) return true;
        if (teamOnLeaveLoading) return true;
        if (canReadProjectsForLoading && projectLoading) return true;
        if (canReadUserMgmtForLoading ? employeeLoading : directoryLoading) return true;
        if (canReadDailyHours) {
            if (calendarTasksLoading) return true;
            if (!isAdminRole && dashYesterdayWorkingHours.loading) return true;
        }
        if (!isAdminRole && canEmployeeAttendanceInsights() && dashboardTodayLogsLoading) {
            return true;
        }
        if (!isAdminRole && canReadLeaveRequests && leaveLoading) return true;
        if (canFetchAdminLeavePending && adminLeavePendingLoading) return true;
        if (canFetchAdminProjectDocPending && adminProjectDocPendingLoading) return true;
        if (
            isAdminRole &&
            canFetchTeamDailyUpdates &&
            getAdminMissedUpdatesCheckDate() &&
            adminOnLeaveLoading
        ) {
            return true;
        }
        return false;
    }, [
        user,
        teamOnLeaveLoading,
        canReadProjectsForLoading,
        projectLoading,
        canReadUserMgmtForLoading,
        employeeLoading,
        directoryLoading,
        canReadDailyHours,
        calendarTasksLoading,
        isAdminRole,
        dashYesterdayWorkingHours.loading,
        canReadLeaveRequests,
        leaveLoading,
        canFetchAdminLeavePending,
        adminLeavePendingLoading,
        canFetchAdminProjectDocPending,
        adminProjectDocPendingLoading,
        canFetchTeamDailyUpdates,
        adminOnLeaveLoading,
        dashboardTodayLogsLoading,
    ]);

    useEffect(() => {
        if (!user || !dashboardApisTriggered || dashboardInitialLoadDoneRef.current) return;
        if (!isAnyDashboardApiPending) {
            dashboardInitialLoadDoneRef.current = true;
            setDashboardInitialLoadDone(true);
        }
    }, [user, dashboardApisTriggered, isAnyDashboardApiPending]);

    const showDashboardSkeleton = !dashboardInitialLoadDone;

    useEffect(() => {
        fetchDashboardPraise();
    }, [fetchDashboardPraise, location.key]);

    useEffect(() => {
        if (!user || !canSendCelebrationEmail) {
            setBirthdayEmailSentUserIds([]);
            setAnniversaryEmailSentUserIds([]);
            return;
        }
        if (Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true) {
            return;
        }
        let cancelled = false;
        Promise.all([fetchBirthdaySentToday(), fetchWorkAnniversarySentToday()])
            .then(([birthdayIds, anniversaryIds]) => {
                if (cancelled) return;
                setBirthdayEmailSentUserIds(birthdayIds);
                setAnniversaryEmailSentUserIds(anniversaryIds);
            })
            .catch(() => {
                if (!cancelled) {
                    setBirthdayEmailSentUserIds([]);
                    setAnniversaryEmailSentUserIds([]);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [user, canSendCelebrationEmail, location.key]);

    useEffect(() => {
        const onPraiseUpdated = () => {
            fetchDashboardPraise();
        };
        window.addEventListener(TEAM_PRAISE_UPDATED_EVENT, onPraiseUpdated);
        return () => window.removeEventListener(TEAM_PRAISE_UPDATED_EVENT, onPraiseUpdated);
    }, [fetchDashboardPraise]);

    useEffect(() => {
        if (!canFetchAdminLeavePending && !isAdminRole) return undefined;

        const onLeaveApproveRefresh = () => {
            fetchAdminLeavePendingCount();
        };

        window.addEventListener(LEAVE_APPROVE_PENDING_REFRESH_EVENT, onLeaveApproveRefresh);
        return () => {
            window.removeEventListener(LEAVE_APPROVE_PENDING_REFRESH_EVENT, onLeaveApproveRefresh);
        };
    }, [isAdminRole, canFetchAdminLeavePending, fetchAdminLeavePendingCount]);

    useEffect(() => {
        if (!isAdminRole) return undefined;

        const onProjectDocPending = (e) => {
            if (typeof e.detail?.count === "number") {
                setAdminProjectDocPendingCount(e.detail.count);
            }
        };

        window.addEventListener(PROJECT_DOC_PENDING_EVENT, onProjectDocPending);
        return () => window.removeEventListener(PROJECT_DOC_PENDING_EVENT, onProjectDocPending);
    }, [isAdminRole]);

    useEffect(() => {
        if (!user) return;
        if (Number(user?.mustChangePassword) === 1 || user?.mustChangePassword === true) {
            return;
        }
        if (location.pathname !== "/dashboard") return;

        const id = setInterval(() => {
            const canReadProjects = checkPermission(permissionType.PROJECT_MANAGEMENT, "read");
            const canReadUsers = checkPermission(permissionType.USER_MANAGEMENT, "read");
            if (canReadProjects) {
                dispatch(getAllProject());
            }
            if (canReadUsers) {
                dispatch(
                    getEmployeesAPI({
                        page: 1,
                        limit: DASHBOARD_EMPLOYEE_PAGE_SIZE,
                        filterBy: "active",
                    })
                );
            } else {
                dispatch(getEmployeeDirectoryAPI());
            }
            if (isAdminRole) {
                fetchAdminLeavePendingCount();
                fetchAdminProjectDocPendingCount();
            } else if (canFetchAdminLeavePending) {
                fetchAdminLeavePendingCount();
            }
            if (isAdminRole) {
                if (canFetchTeamDailyUpdates) {
                    const checkDate = getAdminMissedUpdatesCheckDate();
                    if (checkDate) {
                        const checkDateStr = checkDate.format("YYYY-MM-DD");
                        dispatch(
                            getTasksByDateRangeAPI({
                                startDate: checkDateStr,
                                endDate: checkDateStr,
                                id: "all",
                            })
                        );
                        fetchAdminOnLeaveForCheckDate(checkDateStr);
                    }
                }
            }
            fetchDashboardPraise();
        }, DASHBOARD_POLL_MS);

        return () => clearInterval(id);
    }, [
        dispatch,
        user,
        location.pathname,
        fetchDashboardPraise,
        isAdminRole,
        canFetchTeamDailyUpdates,
        fetchAdminLeavePendingCount,
        fetchAdminProjectDocPendingCount,
        fetchAdminOnLeaveForCheckDate,
    ]);

    const missedTasksThisMonth = useMemo(() => {
        if (!canReadDailyHours) return 0;

        const monthStart = moment().startOf("month");
        const yesterday = moment().subtract(1, "day").endOf("day");
        let count = 0;

        for (let m = moment(monthStart); m.isSameOrBefore(yesterday, "day"); m.add(1, "day")) {
            const dayOfWeek = m.day();
            if (dayOfWeek === 0 || dayOfWeek === 6) continue;

            const dateStr = m.format("YYYY-MM-DD");
            if (isPublicHolidayDate(calendarPublicHolidaysByDate, dateStr)) continue;

            const entries = Array.isArray(calendarTasks?.[dateStr]) ? calendarTasks[dateStr] : [];

            let hasTasks = false;
            if (canReadAllDailyHours) {
                hasTasks = entries.some((entry) => (entry?.taskDetails?.length || 0) > 0);
            } else {
                const userIdStr = user?.id != null ? String(user.id) : "";
                const userEntry = entries.find(
                    (entry) => String(entry?.userDetails?.userId ?? "") === userIdStr
                );
                hasTasks = (userEntry?.taskDetails?.length || 0) > 0;
            }

            if (!hasTasks) count += 1;
        }

        return count;
    }, [calendarTasks, calendarPublicHolidaysByDate, user?.id, canReadAllDailyHours, canReadDailyHours]);

    const adminMissedUpdatesCheck = useMemo(() => {
        if (!isAdminRole) return null;
        const checkDate = getAdminMissedUpdatesCheckDate();
        if (!checkDate) return null;
        const isMonday = moment().isoWeekday() === 1;
        return {
            dateKey: checkDate.format("YYYY-MM-DD"),
            label: isMonday
                ? "Missed Daily Updates (Friday)"
                : "Missed Daily Updates (Yesterday)",
        };
    }, [isAdminRole]);

    const employeesWhoMissedUpdates = useMemo(() => {
        if (!isAdminRole || !canFetchTeamDailyUpdates || !adminMissedUpdatesCheck) return [];

        const { dateKey } = adminMissedUpdatesCheck;
        if (isPublicHolidayDate(calendarPublicHolidaysByDate, dateKey)) return [];

        const dayEntries = Array.isArray(calendarTasks?.[dateKey]) ? calendarTasks[dateKey] : [];
        const team = Array.isArray(employees) ? employees : [];
        if (!team.length) return [];

        return team
            .filter((emp) => !isCeoOrCmoRoleUser(emp))
            .filter((emp) => {
                const id = String(emp?.userId ?? emp?.id ?? "");
                if (!id) return false;
                const leaveInfo = getLeaveInfoForUserDate(
                    calendarLeaveByDate,
                    dateKey,
                    id
                );
                if (isFullLeaveDay(leaveInfo)) return false;
                return !userMeetsDailyWorkRequirement(dayEntries, id, leaveInfo);
            })
            .map((emp) => {
                const id = String(emp?.userId ?? emp?.id ?? "");
                const name = [emp.firstname, emp.lastname].filter(Boolean).join(" ").trim();
                const displayName = name || emp.email || `User ${id}`;
                const leaveSuffix = adminLeaveByUserId[id];
                const hasLeaveNote = Boolean(leaveSuffix);
                return {
                    id,
                    name: displayName,
                    hasLeaveNote,
                    displayLabel: hasLeaveNote ? `${displayName} (${leaveSuffix})` : displayName,
                };
            })
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    }, [
        calendarTasks,
        calendarLeaveByDate,
        calendarPublicHolidaysByDate,
        employees,
        isAdminRole,
        canFetchTeamDailyUpdates,
        adminMissedUpdatesCheck,
        adminLeaveByUserId,
    ]);

    const canReadUserMgmt = checkPermission(permissionType.USER_MANAGEMENT, "read");
    const canReadProjects = checkPermission(permissionType.PROJECT_MANAGEMENT, "read");
    const canApplyLeaveFromDashboard = checkPermission(permissionType.LEAVE_REQUEST, "create");
    const canRequestWfhFromDashboard = checkPermission(permissionType.WFH_REQUESTS, "create");

    const kpiStats = useMemo(() => {
        const totalProjects = projects?.length || 0;

        if (isAdminRole) {
            return [
                {
                    label: "Active Projects",
                    value: totalProjects,
                    variant: "projects",
                    icon: DASHBOARD_KPI_ICONS.projects,
                    path: "/assigned-projects",
                },
                {
                    label: "Pending Leave Requests",
                    value: adminLeavePendingCount,
                    variant: "leaves",
                    icon: DASHBOARD_KPI_ICONS.leaveApprove,
                    path: "/leave_approve",
                    valueClassName: adminLeavePendingCount > 0 ? "dashboard-kpi-value--danger" : undefined,
                },
                {
                    label: adminMissedUpdatesCheck?.label ?? "Missed Daily Updates (Yesterday)",
                    value: employeesWhoMissedUpdates.length,
                    variant: "missed",
                    icon: DASHBOARD_KPI_ICONS.missed,
                    path: "/daily-hours",
                    valueClassName: employeesWhoMissedUpdates.length > 0 ? "dashboard-kpi-value--danger" : undefined,
                    missedEmployees: employeesWhoMissedUpdates,
                },
                {
                    label: "Project Doc Edit Requests",
                    value: adminProjectDocPendingCount,
                    variant: "docs",
                    icon: DASHBOARD_KPI_ICONS.docs,
                    path: "/project-documentation",
                    valueClassName: adminProjectDocPendingCount > 0 ? "dashboard-kpi-value--danger" : undefined,
                },
            ];
        }

        const pendingLeaves = leavePagination?.totalData || 0;
        const employeeKpis = [
            {
                label: "My Projects",
                value: totalProjects,
                variant: "projects",
                icon: DASHBOARD_KPI_ICONS.projects,
                path: canReadProjects ? "/assigned-projects" : undefined,
            },
            {
                label: "Missed Tasks (This Month)",
                value: missedTasksThisMonth,
                variant: "missed",
                icon: DASHBOARD_KPI_ICONS.missed,
                path: canReadDailyHours ? "/daily-hours" : undefined,
                valueClassName: missedTasksThisMonth > 0 ? "dashboard-kpi-value--danger" : undefined,
            },
        ];

        if (isHrDashboard && canFetchAdminLeavePending) {
            employeeKpis.push({
                label: "Pending Leave Requests",
                value: adminLeavePendingCount,
                variant: "leaves",
                icon: DASHBOARD_KPI_ICONS.leaveApprove,
                path: "/leave_approve",
                valueClassName: adminLeavePendingCount > 0 ? "dashboard-kpi-value--danger" : undefined,
            });
        }

        employeeKpis.push({
            label: "Leave Requests",
            value: pendingLeaves,
            variant: "leaves",
            icon: DASHBOARD_KPI_ICONS.leaves,
            path: "/leave_request",
            valueClassName: pendingLeaves > 0 ? "dashboard-kpi-value--danger" : undefined,
        });

        return employeeKpis;
    }, [
        projects,
        isAdminRole,
        isHrDashboard,
        canFetchAdminLeavePending,
        adminLeavePendingCount,
        adminMissedUpdatesCheck,
        employeesWhoMissedUpdates,
        adminProjectDocPendingCount,
        missedTasksThisMonth,
        leavePagination,
        canReadProjects,
        canReadDailyHours,
    ]);

    const userDisplayName = useMemo(() => {
        const name = [user?.firstname, user?.lastname].filter(Boolean).join(" ").trim();
        return name || user?.email || "there";
    }, [user?.firstname, user?.lastname, user?.email]);

    const adminQuickActions = useMemo(() => {
        if (!isAdminRole) return [];
        const actions = [];
        if (canFetchAdminLeavePending) {
            actions.push({
                key: "leave-approve",
                title: "Review Leave Requests",
                subtitle: "Approve or decline pending requests",
                icon: DASHBOARD_KPI_ICONS.leaveApprove,
                path: "/leave_approve",
                badge: adminLeavePendingCount,
                variant: "amber",
            });
        }
        if (canFetchAdminProjectDocPending) {
            actions.push({
                key: "project-docs",
                title: "Project Documentation",
                subtitle: "Review edit requests",
                icon: DASHBOARD_KPI_ICONS.docs,
                path: "/project-documentation",
                badge: adminProjectDocPendingCount,
                variant: "violet",
            });
        }
        if (canReadUserMgmt) {
            actions.push({
                key: "employees",
                title: "Team Directory",
                subtitle: "View and manage employees",
                icon: "/svg/Icons/employees-icon.svg",
                path: "/employees",
                variant: "blue",
            });
        }
        if (canReadProjects) {
            actions.push({
                key: "projects",
                title: "Projects",
                subtitle: "Assignments and timelines",
                icon: DASHBOARD_KPI_ICONS.projects,
                path: "/assigned-projects",
                variant: "indigo",
            });
        }
        if (canReadDailyHours) {
            actions.push({
                key: "daily-hours",
                title: "Daily Work Updates",
                subtitle: "Team hours and task logs",
                icon: DASHBOARD_KPI_ICONS.missed,
                path: "/daily-hours",
                variant: "teal",
            });
        }
        return actions;
    }, [
        isAdminRole,
        canFetchAdminLeavePending,
        canFetchAdminProjectDocPending,
        canReadUserMgmt,
        canReadProjects,
        canReadDailyHours,
        adminLeavePendingCount,
        adminProjectDocPendingCount,
    ]);

    const hrQuickActions = useMemo(() => {
        if (!isHrDashboard) return [];

        const actions = [
            {
                key: "team-directory",
                title: "Team Directory",
                subtitle: "Browse employees and contacts",
                icon: "/svg/Icons/employees-icon.svg",
                path: "/employee-directory",
                variant: "blue",
            },
        ];

        if (canFetchAdminLeavePending) {
            actions.push({
                key: "leave-approve",
                title: "Review Leave Requests",
                subtitle: "Approve or decline pending requests",
                icon: DASHBOARD_KPI_ICONS.leaveApprove,
                path: "/leave_approve",
                badge: adminLeavePendingCount,
                variant: "amber",
            });
        }

        if (canRequestWfhFromDashboard) {
            actions.push({
                key: "request-wfh",
                title: "Request WFH",
                subtitle: "Work from home for selected days",
                icon: DASHBOARD_KPI_ICONS.leaves,
                variant: "teal",
                action: "wfh",
            });
        }
        if (canReadDailyHours) {
            actions.push({
                key: "daily-hours",
                title: "Add Daily Work Updates",
                subtitle: "Log hours and tasks for today",
                icon: DASHBOARD_KPI_ICONS.missed,
                variant: "indigo",
                action: "hours",
            });
        }
        if (canReadProjects) {
            actions.push({
                key: "projects",
                title: "My Projects",
                subtitle: "View assignments and timelines",
                icon: DASHBOARD_KPI_ICONS.projects,
                path: "/assigned-projects",
                variant: "violet",
            });
        }
        if (canApplyLeaveFromDashboard) {
            actions.push({
                key: "apply-leave",
                title: "Apply Leave",
                subtitle: "Submit a new leave request",
                icon: DASHBOARD_KPI_ICONS.leaves,
                variant: "amber",
                action: "leave",
            });
        }

        return actions;
    }, [
        isHrDashboard,
        canFetchAdminLeavePending,
        canApplyLeaveFromDashboard,
        canRequestWfhFromDashboard,
        canReadDailyHours,
        canReadProjects,
        adminLeavePendingCount,
    ]);

    const employeeQuickActions = useMemo(() => {
        if (isAdminRole || isHrDashboard) return [];
        const actions = [];

        if (canRequestWfhFromDashboard) {
            actions.push({
                key: "request-wfh",
                title: "Request WFH",
                subtitle: "Work from home for selected days",
                icon: DASHBOARD_KPI_ICONS.leaves,
                variant: "teal",
                action: "wfh",
            });
        }
        if (canReadDailyHours) {
            actions.push({
                key: "daily-hours",
                title: "Add Daily Work Updates",
                subtitle: "Log hours and tasks for today",
                icon: DASHBOARD_KPI_ICONS.missed,
                variant: "indigo",
                action: "hours",
            });
        }
        if (canReadProjects) {
            actions.push({
                key: "projects",
                title: "My Projects",
                subtitle: "View assignments and timelines",
                icon: DASHBOARD_KPI_ICONS.projects,
                path: "/assigned-projects",
                variant: "blue",
            });
        }
        if (canApplyLeaveFromDashboard) {
            actions.push({
                key: "apply-leave",
                title: "Apply Leave",
                subtitle: "Submit a new leave request",
                icon: DASHBOARD_KPI_ICONS.leaves,
                variant: "amber",
                action: "leave",
            });
        }
        return actions;
    }, [
        isAdminRole,
        canApplyLeaveFromDashboard,
        canRequestWfhFromDashboard,
        canReadDailyHours,
        canReadProjects,
    ]);

    const teamForDashboardWidgets = useMemo(() => {
        if (canReadUserMgmt) {
            return Array.isArray(employees) ? employees : [];
        }
        return Array.isArray(directoryUsers) ? directoryUsers : [];
    }, [canReadUserMgmt, employees, directoryUsers]);

    const handleApplyLeave = () => {
        navigate("/leave_request");
        if (typeof openLeaveDrawer === "function") {
            openLeaveDrawer();
        }
    };

    const handleRequestWfhFromDashboard = () => {
        navigate("/leave_request");
        if (typeof openLeaveDrawer === "function") {
            openLeaveDrawer({ workLocation: "wfh" });
        }
    };

    const handleAddDailyHour = () => {
        if (typeof openHoursDrawer === "function") {
            openHoursDrawer();
        }
    };

    const handleEmployeeQuickAction = (action) => {
        if (action.path) {
            navigate(action.path);
            return;
        }
        if (action.action === "leave") {
            handleApplyLeave();
        } else if (action.action === "wfh") {
            handleRequestWfhFromDashboard();
        } else if (action.action === "hours") {
            handleAddDailyHour();
        }
    };

    const attendanceMorningTile = useMemo(() => {
        if (isAdminRole) return null;
        if (!canEmployeeAttendanceInsights()) return null;

        const now = moment();
        const morningWindowEnd = now.clone().startOf("day").hour(10).minute(0).second(0);
        const afterMorningWindow = now.isSameOrAfter(morningWindowEnd);
        const morning = getTodayMorningInPunch(dashboardTodayLogs);

        if (dashboardTodayLogsLoading) {
            return { variant: "loading", title: "Attendance", text: "Checking today’s machine punches…" };
        }

        if (morning) {
            if (morning.c.isLate) {
                return {
                    variant: "warning",
                    title: "Late arrival",
                    text: `In time recorded at ${formatPunchDisplay(morning.row.punchTime)}.`,
                };
            }
            return {
                variant: "success",
                title: "Punched in",
                text: `In time at ${formatPunchDisplay(morning.row.punchTime)}.`,
            };
        }

        if (afterMorningWindow) {
            return {
                variant: "alert",
                title: "Missing punch-in",
                text: "No machine punch-in was recorded today before 4:00 PM.",
            };
        }

        return {
            variant: "info",
            title: "Morning punch-in",
            text: "Punch in at the machine between 9:00 and 10:00 AM.",
        };
    }, [dashboardTodayLogs, dashboardTodayLogsLoading, isAdminRole]);

    const yesterdayShortfallTile = useMemo(() => {
        if (isAdminRole) return null;
        if (!canEmployeeAttendanceInsights()) return null;
        if (!canReadDailyHours) return null;
        const y = moment().subtract(1, "day");
        if (y.isoWeekday() >= 6) return null;

        if (calendarTasksLoading || dashYesterdayWorkingHours.loading) {
            return {
                variant: "loading",
                title: "Yesterday’s hours",
                text: "Checking yesterday's daily hours…",
            };
        }

        const dateKey = y.format("YYYY-MM-DD");
        if (isPublicHolidayDate(calendarPublicHolidaysByDate, dateKey)) return null;

        const dayEntries = Array.isArray(calendarTasks?.[dateKey]) ? calendarTasks[dateKey] : [];
        const userIdStr = user?.id != null ? String(user.id) : "";
        const dayEntryForUser = dayEntries.find(
            (entry) => String(entry?.userDetails?.userId ?? "") === userIdStr
        );
        const totalMinutes = (dayEntryForUser?.taskDetails || []).reduce(
            (sum, task) => sum + Number(task?.duration || 0),
            0
        );
        const expectedHours = dashYesterdayWorkingHours.hours;
        if (expectedHours <= 0) return null;

        const expectedMinutes = expectedHours * 60;
        if (totalMinutes >= expectedMinutes) return null;

        return {
            variant: "alert",
            title: "Yesterday’s Work Updates",
            text: "Your yesterday’s work updates have not been filled in yet. Please update and submit your updates as soon as possible.",
        };
    }, [
        calendarTasks,
        calendarPublicHolidaysByDate,
        calendarTasksLoading,
        dashYesterdayWorkingHours.loading,
        dashYesterdayWorkingHours.hours,
        isAdminRole,
        user?.id,
        canReadDailyHours,
    ]);

    const showAttendanceRow = Boolean(attendanceMorningTile || yesterdayShortfallTile);

    const handleDashboardKpiNavigate = (path) => {
        if (path) navigate(path);
    };

    const dashboardQuickActions = isAdminRole
        ? adminQuickActions
        : isHrDashboard
            ? hrQuickActions
            : employeeQuickActions;

    const handleQuickActionClick = (action) => {
        if (action.path) {
            navigate(action.path);
            return;
        }
        handleEmployeeQuickAction(action);
    };

    const renderQuickActions = () => {
        if (!dashboardQuickActions.length) return null;
        return (
            <section className="dashboard-quick-actions-section" aria-label="Quick actions">
                <h2 className="dashboard-quick-actions-title">Quick actions</h2>
                <div className="dashboard-quick-actions-grid">
                    {dashboardQuickActions.map((action) => (
                        <button
                            key={action.key}
                            type="button"
                            className={[
                                "dashboard-action-card",
                                `dashboard-action-card--${action.variant}`,
                            ].join(" ")}
                            onClick={() => handleQuickActionClick(action)}
                        >
                            <span className="dashboard-action-icon-wrap">
                                <img src={action.icon} alt="" aria-hidden="true" />
                            </span>
                            <span className="dashboard-action-body">
                                <span className="dashboard-action-title">{action.title}</span>
                                <span className="dashboard-action-subtitle">{action.subtitle}</span>
                            </span>
                            {typeof action.badge === "number" && action.badge > 0 ? (
                                <span className="dashboard-action-badge">{action.badge}</span>
                            ) : null}
                        </button>
                    ))}
                </div>
            </section>
        );
    };

    return (
        <div
            className={[
                "dashboard-page",
                isAdminRole
                    ? "dashboard-page--admin"
                    : isHrDashboard
                        ? "dashboard-page--hr"
                        : "dashboard-page--employee",
            ].join(" ")}
        >
            {showDashboardSkeleton ? (
                <DashboardSkeleton isAdmin={isAdminRole} isHr={isHrDashboard} />
            ) : (
            <>
            {isAdminRole ? (
                <div className="dashboard-hero dashboard-hero--admin">
                    <div className="dashboard-hero-content">
                        <span className="dashboard-hero-badge">Admin overview</span>
                        <h1 className="dashboard-hero-title">
                            {getDashboardGreeting()}, {userDisplayName}
                        </h1>
                        <p className="dashboard-hero-subtitle">
                            {moment().format("dddd, MMMM D, YYYY")} · Monitor team activity, approvals, and project health at a glance.
                        </p>
                    </div>
                    <div className="dashboard-hero-decoration" aria-hidden="true">
                        <span className="dashboard-hero-orb dashboard-hero-orb--1" />
                        <span className="dashboard-hero-orb dashboard-hero-orb--2" />
                        <span className="dashboard-hero-orb dashboard-hero-orb--3" />
                    </div>
                </div>
            ) : (
                <div className="dashboard-hero dashboard-hero--employee">
                    <div className="dashboard-hero-content">
                        <span className="dashboard-hero-badge">
                            {isHrDashboard ? "HR workspace" : "Your workspace"}
                        </span>
                        <h1 className="dashboard-hero-title">
                            {getDashboardGreeting()}, {userDisplayName}
                        </h1>
                        <p className="dashboard-hero-subtitle">
                            {moment().format("dddd, MMMM D, YYYY")}
                            {isHrDashboard
                                ? " · Review pending leave, support the team, and manage your own updates."
                                : " · Track your projects, leave, and daily updates in one place."}
                        </p>
                    </div>
                    <div className="dashboard-hero-decoration" aria-hidden="true">
                        <span className="dashboard-hero-orb dashboard-hero-orb--1" />
                        <span className="dashboard-hero-orb dashboard-hero-orb--2" />
                        <span className="dashboard-hero-orb dashboard-hero-orb--3" />
                    </div>
                </div>
            )}
            <DashboardCelebrations
                employees={teamForDashboardWidgets}
                canSendCelebrationEmail={canSendCelebrationEmail}
                sentBirthdayEmailUserIds={birthdayEmailSentUserIds}
                onBirthdayEmailSent={markBirthdayEmailSent}
                sentAnniversaryEmailUserIds={anniversaryEmailSentUserIds}
                onAnniversaryEmailSent={markAnniversaryEmailSent}
            />
            <DashboardPraiseBanner
                message={dashboardPraise?.message}
                praiseLabel={dashboardPraise?.praiseLabel}
                authorName={dashboardPraise?.authorName}
                updatedAt={dashboardPraise?.updatedAt}
            />
            <div
                className={[
                    "dashboard-kpi-grid",
                    isAdminRole
                        ? "dashboard-kpi-grid--admin"
                        : isHrDashboard
                            ? "dashboard-kpi-grid--hr"
                            : "dashboard-kpi-grid--employee",
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {kpiStats.map((item) => {
                    const isClickable = Boolean(item.path);
                    const cardClassName = [
                        "dashboard-kpi-card",
                        item.variant ? `dashboard-kpi-card--${item.variant}` : "",
                        isClickable ? "dashboard-kpi-card--clickable" : "",
                    ]
                        .filter(Boolean)
                        .join(" ");

                    return (
                        <div
                            key={item.label}
                            className={cardClassName}
                            role={isClickable ? "button" : undefined}
                            tabIndex={isClickable ? 0 : undefined}
                            onClick={isClickable ? () => handleDashboardKpiNavigate(item.path) : undefined}
                            onKeyDown={
                                isClickable
                                    ? (e) => {
                                        if (e.key === "Enter" || e.key === " ") {
                                            e.preventDefault();
                                            handleDashboardKpiNavigate(item.path);
                                        }
                                    }
                                    : undefined
                            }
                        >
                            {item.variant && item.icon ? (
                                <div className="dashboard-kpi-card-top">
                                    <div className="dashboard-kpi-icon-wrap">
                                        <img src={item.icon} alt="" aria-hidden="true" />
                                    </div>
                                    {isClickable ? (
                                        <span className="dashboard-kpi-open-hint">Open</span>
                                    ) : null}
                                </div>
                            ) : null}
                            <div className="dashboard-kpi-label">{item.label}</div>
                            <div
                                className={[
                                    "dashboard-kpi-value",
                                    item.valueClassName,
                                ]
                                    .filter(Boolean)
                                    .join(" ")}
                            >
                                {item.value}
                            </div>
                            {Array.isArray(item.missedEmployees) && item.missedEmployees.length > 0 ? (
                                <ul className="dashboard-kpi-missed-names" aria-label="Employees who missed updates">
                                    {item.missedEmployees.map((emp) => (
                                        <li
                                            key={emp.id}
                                            className={
                                                emp.hasLeaveNote
                                                    ? "dashboard-kpi-missed-names-item--on-leave"
                                                    : undefined
                                            }
                                        >
                                            {emp.displayLabel}
                                        </li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            {showAttendanceRow ? (
                <div className="dashboard-attendance-slot">
                    {attendanceMorningTile ? (
                        <div
                            className={`dashboard-attendance-tile dashboard-attendance-tile--${attendanceMorningTile.variant}`}
                            role="status"
                        >
                            <div className="dashboard-attendance-tile-title">{attendanceMorningTile.title}</div>
                            <div className="dashboard-attendance-tile-text">{attendanceMorningTile.text}</div>
                        </div>
                    ) : null}
                    {yesterdayShortfallTile ? (
                        <div
                            className={`dashboard-attendance-tile dashboard-attendance-tile--${yesterdayShortfallTile.variant}`}
                            role="status"
                        >
                            <div className="dashboard-attendance-tile-title">{yesterdayShortfallTile.title}</div>
                            <div className="dashboard-attendance-tile-text">{yesterdayShortfallTile.text}</div>
                        </div>
                    ) : null}
                </div>
            ) : null}
            {renderQuickActions()}
            <ProjectTimeSpent />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 py-6 dashboard-bottom-cards">
                <EmployeeLeave />
                <UpcomingBirthdays
                    employees={teamForDashboardWidgets}
                    canSendCelebrationEmail={canSendCelebrationEmail}
                    sentBirthdayEmailUserIds={birthdayEmailSentUserIds}
                    onBirthdayEmailSent={markBirthdayEmailSent}
                />
                <UpcomingAnniversary
                    employees={teamForDashboardWidgets}
                    canSendCelebrationEmail={canSendCelebrationEmail}
                    sentAnniversaryEmailUserIds={anniversaryEmailSentUserIds}
                    onAnniversaryEmailSent={markAnniversaryEmailSent}
                />
            </div>
            </>
            )}
        </div>
    )
}

export default DashboardPage
